import { QueryClient } from '@tanstack/react-query';
import { AppErrorPage } from './AppErrorPage.js';
import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { lazy } from 'react';
import AdminLayout from '../admin/AdminLayout.js';
import AcceptInvitationPage from '../garage/pages/AcceptInvitationPage.js';
import AccountPage from '../garage/pages/AccountPage.js';
import AgendaPage from '../garage/pages/AgendaPage.js';
import BasePage from '../garage/pages/BasePage.js';
import CustomerPage from '../garage/pages/CustomerPage.js';
import ChooseGaragePage from '../garage/pages/ChooseGaragePage.js';
import DossiersPage from '../garage/pages/DossiersPage.js';
import AppointmentsPage from '../garage/pages/AppointmentsPage.js';
import FiguresPage from '../garage/pages/FiguresPage.js';
import ManagementPage from '../garage/pages/ManagementPage.js';
import ForgotPasswordPage from '../garage/pages/ForgotPasswordPage.js';
import HomePage from '../garage/pages/HomePage.js';
import InboxPage from '../garage/pages/InboxPage.js';
import InterventionPage from '../garage/pages/InterventionPage.js';
import LoginPage from '../garage/pages/LoginPage.js';
import NewEntryPage from '../garage/pages/NewEntryPage.js';
import OpenConversationPage, { OpenComplementsPage } from '../garage/pages/OpenConversationPage.js';
import ParkPage from '../garage/pages/ParkPage.js';
import ResetPasswordPage from '../garage/pages/ResetPasswordPage.js';
import RootPage, { LegacyRedirect } from '../garage/pages/RootPage.js';
import VehiclePage from '../garage/pages/VehiclePage.js';
import PortalPage from '../portal/pages/PortalPage.js';
import { LegalNoticePage, PlatformPrivacyPage } from '../public/LegalPages.js';
import { PLATFORM_LEGAL_PATHS } from '@garage/shared';
import { GarageSpace, NotFoundPage, PortalLayout } from './layout.js';

// Console chargée à la demande : ni le portail ni l'atelier n'en téléchargent le code.
const GaragesPage = lazy(() => import('../admin/pages/GaragesPage.js'));
const NewGaragePage = lazy(() => import('../admin/pages/NewGaragePage.js'));
const GaragePage = lazy(() => import('../admin/pages/GaragePage.js'));
const SupportPage = lazy(() => import('../admin/pages/SupportPage.js'));
const DemosPage = lazy(() => import('../admin/pages/DemosPage.js'));
const DemoDetailPage = lazy(() => import('../admin/pages/DemoDetailPage.js'));
// Vague 7 : démonstration générique et page d'arrivée d'un lien de démonstration (jeton dans le fragment).
const DemoPage = lazy(() => import('../garage/pages/DemoPage.js'));
const DemoLinkPage = lazy(() => import('../garage/pages/DemoLinkPage.js'));
const PrivacyPage = lazy(() => import('../portal/pages/PrivacyPage.js'));
// 08/10/2026 : centre d'aide (équipe `/aide`, clients `/aide/client`), public, chargé à la demande.
const TeamHelpHome = lazy(() => import('../help/HelpPages.js').then((m) => ({ default: m.TeamHelpHome })));
const TeamHelpArticle = lazy(() => import('../help/HelpPages.js').then((m) => ({ default: m.TeamHelpArticle })));
const ClientHelpHome = lazy(() => import('../help/HelpPages.js').then((m) => ({ default: m.ClientHelpHome })));
const ClientHelpArticle = lazy(() => import('../help/HelpPages.js').then((m) => ({ default: m.ClientHelpArticle })));

/**
 * Routes du front (vague 5). Trois espaces dans une seule application :
 * - espace garage : `/g/:slug/…` (le slug sert à naviguer, aucun droit) ; `/` mène à la connexion ou
 *   à l'espace de travail ; les anciennes adresses (`/parc`, `/reglages`…) sont redirigées ;
 * - console plateforme : `/admin/…` ;
 * - portail client : `/p` (jeton dans le fragment `#…`, jamais dans le chemin ni la requête).
 */
export const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1 } } });

const root = createRootRoute({ component: Outlet, notFoundComponent: NotFoundPage });

const index = createRoute({ getParentRoute: () => root, path: '/', component: RootPage });

// Pages publiques (sans session) et choix du garage
const login = createRoute({ getParentRoute: () => root, path: '/login', component: LoginPage });
const forgot = createRoute({ getParentRoute: () => root, path: '/forgot-password', component: ForgotPasswordPage });
const reset = createRoute({ getParentRoute: () => root, path: '/reset-password', component: ResetPasswordPage });
const invitation = createRoute({ getParentRoute: () => root, path: '/invitation', component: AcceptInvitationPage });
const chooseGarage = createRoute({ getParentRoute: () => root, path: '/choose-garage', component: ChooseGaragePage });
const demo = createRoute({ getParentRoute: () => root, path: '/demo', component: DemoPage });
const demoLink = createRoute({ getParentRoute: () => root, path: '/demo/lien', component: DemoLinkPage });
// Vague 7 : pages légales de la plateforme (sans marque de garage), sur l'espace garage et la page de suivi.
const legalNotice = createRoute({ getParentRoute: () => root, path: PLATFORM_LEGAL_PATHS.legalNotice, component: LegalNoticePage });
const platformPrivacy = createRoute({ getParentRoute: () => root, path: PLATFORM_LEGAL_PATHS.privacy, component: PlatformPrivacyPage });
const helpTeam = createRoute({ getParentRoute: () => root, path: '/aide', component: TeamHelpHome });
const helpTeamArticle = createRoute({ getParentRoute: () => root, path: '/aide/$article', component: TeamHelpArticle });
const helpClient = createRoute({ getParentRoute: () => root, path: '/aide/client', component: ClientHelpHome });
const helpClientArticle = createRoute({ getParentRoute: () => root, path: '/aide/client/$article', component: ClientHelpArticle });
const openConversation = createRoute({
  getParentRoute: () => root,
  path: '/ouvrir/conversation/$interventionId',
  component: OpenConversationPage,
});
const openComplements = createRoute({
  getParentRoute: () => root,
  path: '/ouvrir/travaux-complementaires/$interventionId',
  component: OpenComplementsPage,
});

// Console plateforme
const admin = createRoute({ getParentRoute: () => root, path: '/admin', component: AdminLayout });
const adminHome = createRoute({ getParentRoute: () => admin, path: '/', component: GaragesPage });
const adminNew = createRoute({ getParentRoute: () => admin, path: '/garages/new', component: NewGaragePage });
const adminGarage = createRoute({ getParentRoute: () => admin, path: '/garages/$slug', component: GaragePage });
const adminSupport = createRoute({ getParentRoute: () => admin, path: '/support', component: SupportPage });
const adminDemos = createRoute({ getParentRoute: () => admin, path: '/demos', component: DemosPage });
const adminDemo = createRoute({ getParentRoute: () => admin, path: '/demos/$slug', component: DemoDetailPage });

// Espace garage
const garage = createRoute({ getParentRoute: () => root, path: '/g/$slug', component: GarageSpace });
const home = createRoute({ getParentRoute: () => garage, path: '/', component: HomePage });
const park = createRoute({ getParentRoute: () => garage, path: '/parc', component: ParkPage });
// 03/10/2026 : « Interventions » devient « Rendez-vous » ; `/dossiers` redirige (Rendez-vous ou Agenda).
const dossiers = createRoute({ getParentRoute: () => garage, path: '/dossiers', component: DossiersPage });
const appointments = createRoute({ getParentRoute: () => garage, path: '/rendez-vous', component: AppointmentsPage });
const entry = createRoute({ getParentRoute: () => garage, path: '/entree', component: NewEntryPage });
const inbox = createRoute({ getParentRoute: () => garage, path: '/messagerie', component: InboxPage });
// Vague 7 : agenda (module facultatif).
const agenda = createRoute({ getParentRoute: () => garage, path: '/agenda', component: AgendaPage });
// 02/10/2026 : « Chiffres » (ex-widget « Chiffres de l'atelier » de l'Accueil).
const figures = createRoute({ getParentRoute: () => garage, path: '/chiffres', component: FiguresPage });
// 03/10/2026 : « Gestion » (remplace « Chiffres », dont l'adresse redirige vers l'onglet Volume financier).
const management = createRoute({ getParentRoute: () => garage, path: '/gestion', component: ManagementPage });
const managementTab = createRoute({ getParentRoute: () => garage, path: '/gestion/$tab', component: ManagementPage });
const conversation = createRoute({ getParentRoute: () => garage, path: '/messagerie/$interventionId', component: InboxPage });
const intervention = createRoute({ getParentRoute: () => garage, path: '/interventions/$interventionId', component: InterventionPage });
const vehicle = createRoute({ getParentRoute: () => garage, path: '/vehicules/$vehicleId', component: VehiclePage });
// Base garage (02/10/2026) : annuaire des clients et des véhicules, et fiche client.
const base = createRoute({ getParentRoute: () => garage, path: '/base', component: BasePage });
const customer = createRoute({ getParentRoute: () => garage, path: '/clients/$customerId', component: CustomerPage });
const account = createRoute({ getParentRoute: () => garage, path: '/compte', component: AccountPage });
const accountSection = createRoute({ getParentRoute: () => garage, path: '/compte/$section', component: AccountPage });
const supportAccess = createRoute({ getParentRoute: () => garage, path: '/compte/support/$accessId', component: AccountPage });

// Anciennes adresses (avant la vague 5)
const legacy = ['/parc', '/dossiers', '/entree', '/equipe', '/reglages', '/interventions/$interventionId', '/vehicules/$vehicleId'].map(
  (path) => createRoute({ getParentRoute: () => root, path, component: LegacyRedirect }),
);

// Portail client
const portal = createRoute({ getParentRoute: () => root, id: 'portal', component: PortalLayout });
const portalHome = createRoute({ getParentRoute: () => portal, path: '/p', component: PortalPage });
// Vague 6 : politique de confidentialité du garage, publique, adressée par une clé aléatoire (jamais le slug).
const privacy = createRoute({ getParentRoute: () => portal, path: '/confidentialite/$key', component: PrivacyPage });

export const routeTree = root.addChildren([
  index,
  login,
  forgot,
  reset,
  invitation,
  chooseGarage,
  demo,
  demoLink,
  legalNotice,
  platformPrivacy,
  helpTeam,
  helpTeamArticle,
  helpClient,
  helpClientArticle,
  openConversation,
  openComplements,
  admin.addChildren([adminHome, adminNew, adminGarage, adminSupport, adminDemos, adminDemo]),
  garage.addChildren([home, agenda, figures, management, managementTab, park, dossiers, appointments, base, entry, inbox, conversation, intervention, vehicle, customer, account, accountSection, supportAccess]),
  ...legacy,
  portal.addChildren([portalHome, privacy]),
]);

export const router = createRouter({ routeTree, defaultErrorComponent: AppErrorPage });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
